import * as React from 'react';
import { UserContext } from '../../Context/UserContext';
import { WebAPI } from '../../../MedicareProAPI';

export interface SendgridMessage {
  from_email: string;
  msg_id: string;
  subject: string;
  to_email: string;
  status: string;
  opens_count: number;
  clicks_count: number;
  last_event_time: string;
}

const PAGE_SIZE = 20;

export const UserEmailActivity: React.FC = () => {
  const userData = React.useContext(UserContext);

  const webAPI = React.useMemo(() => {
    const r = new WebAPI();
    r.setAccessToken(userData.userAccessToken);
    return r;
  }, [userData.userAccessToken]);

  const [rows, setRows] = React.useState<SendgridMessage[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [loadingMore, setLoadingMore] = React.useState<boolean>(false);
  const [offset, setOffset] = React.useState<number>(0);
  const [hasMore, setHasMore] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);

  const [searchEmail, setSearchEmail] = React.useState<string>('');
  const [appliedEmail, setAppliedEmail] = React.useState<string>('');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setRows([]);
      setOffset(0);
      setHasMore(true);
      try {
        const criteria = { toEmail: appliedEmail || '' };
        const res = await webAPI.getUserEmailActivity({
          limit: PAGE_SIZE,
          offset: 0,
          criteria: criteria,
        });
        if (cancelled) return;
        const page: SendgridMessage[] = res?.messages ?? [];
        setRows(page);
        setOffset(page.length);
        setHasMore(page.length === PAGE_SIZE);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load email activity');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [webAPI, appliedEmail]);

  const loadMore = React.useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const criteria = { toEmail: appliedEmail || '' };
      const res = await webAPI.getUserEmailActivity({
        limit: PAGE_SIZE,
        offset,
        criteria: criteria,
      });
      const page: SendgridMessage[] = res?.messages ?? [];
      setRows((prev) => prev.concat(page));
      setOffset((prev) => prev + page.length);
      setHasMore(page.length === PAGE_SIZE);
    } catch (e: any) {
      setError(e?.message || 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  }, [webAPI, offset, hasMore, loadingMore, appliedEmail]);

  const onSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    setAppliedEmail(searchEmail.trim());
  };

  const clearSearch = () => {
    setSearchEmail('');
    setAppliedEmail('');
  };

  const fmt = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(+d) ? iso : d.toLocaleString();
  };

  return (
    <>
      <div className="row">
        <div className="col-sm-12 col-md-12 px-2 sm-resp">
          <div className="card card-tall">
            <div className="card-header font-weight-bold py-2">Email Activity</div>

            {loading && (
              <div className="col-12">
                <div className="alert small alert-info" role="alert">
                  <i className="fas fa-spinner fa-spin ml-2" /> Loading…
                </div>
              </div>
            )}

            {error && !loading && (
              <div className="col-12">
                <div className="alert alert-danger small py-1">{error}</div>
              </div>
            )}

            {!loading && rows.length === 0 && !error && (
              <div className="col-12">
                <div>
                  <i className="far fa-exclamation-circle" /> No email activity
                </div>
              </div>
            )}

            {rows.length > 0 && (
              <div className="card-body pt-1" id="emailActivity">
                {/* Search bar */}
                <div className="row mb-2">
                  <div className="col-12 d-flex align-items-center">
                    <form className="form-inline w-100" onSubmit={onSearchSubmit}>
                      <input
                        type="email"
                        inputMode="email"
                        className="form-control form-control-sm mr-2"
                        style={{ maxWidth: 320 }}
                        placeholder="Filter by recipient email"
                        value={searchEmail}
                        onChange={(e) => setSearchEmail(e.target.value)}
                      />
                      <button type="submit" className="btn btn-sm btn-primary mr-2">
                        Search
                      </button>
                      {appliedEmail && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={clearSearch}
                        >
                          Clear
                        </button>
                      )}
                      {appliedEmail && (
                        <span className="small text-muted ml-2">Active filter: {appliedEmail}</span>
                      )}
                    </form>
                  </div>
                </div>
                <table className="table table-sm table-borderless header-fixed dataTable no-footer">
                  <thead>
                    <tr>
                      <th>Processed at</th>
                      <th>Recipient Email</th>
                      <th>Subject Line</th>
                      <th>Status</th>
                      <th>Opens</th>
                      <th>Clicks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((m) => (
                      <tr key={m.msg_id}>
                        <td>{fmt(m.last_event_time)}</td>
                        <td>{m.to_email}</td>
                        <td className="text-truncate" style={{ maxWidth: 360 }}>
                          {m.subject}
                        </td>
                        <td>{m.status}</td>
                        <td>{m.opens_count}</td>
                        <td>{m.clicks_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="d-flex align-items-center justify-content-center mt-2">
                  {hasMore ? (
                    <button
                      className="btn btn-sm btn-outline-primary"
                      disabled={loadingMore}
                      onClick={loadMore}
                    >
                      {loadingMore ? (
                        <>
                          <i className="fal fa-spinner fa-spin mr-1" /> Loading…
                        </>
                      ) : (
                        'Load more'
                      )}
                    </button>
                  ) : (
                    <span className="text-muted small">No more results</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
